import React from "react";
import { Edit, useForm } from "@refinedev/antd";
import {
  Form,
  Input,
  InputNumber,
  Switch,
  Select,
  Space,
  Typography,
  Checkbox,
  Row,
  Col,
  Card,
} from "antd";

const { TextArea } = Input;
const { Text } = Typography;

export const MembershipTierEdit: React.FC = () => {
  const { formProps, saveButtonProps, queryResult } = useForm();
  const record = queryResult?.data?.data;

  return (
    <Edit saveButtonProps={saveButtonProps}>
      <Form {...formProps} layout="vertical">
        <Row gutter={16}>
          <Col xs={24} lg={12}>
            <Card title="基本信息" size="small">
              <Form.Item
                label="等级标识"
                name="name"
                rules={[
                  { required: true, message: "请输入等级标识" },
                  {
                    pattern: /^[a-z_]+$/,
                    message: "只能使用小写字母和下划线",
                  },
                ]}
                tooltip="系统内部标识，不建议修改"
              >
                <Input placeholder="例如: premium" disabled={record?.name === "free"} />
              </Form.Item>

              <Form.Item
                label="显示名称"
                name="display_name"
                rules={[{ required: true, message: "请输入显示名称" }]}
              >
                <Input placeholder="例如: 高级会员" />
              </Form.Item>

              <Form.Item label="描述" name="description">
                <TextArea rows={3} placeholder="简短描述该等级的特点" />
              </Form.Item>

              <Form.Item
                label="排序权重"
                name="display_order"
                rules={[{ required: true, message: "请输入排序权重" }]}
                tooltip="数字越小越靠前显示"
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  max={999}
                  placeholder="0"
                />
              </Form.Item>

              <Form.Item
                label="是否启用"
                name="is_active"
                valuePropName="checked"
              >
                <Switch checkedChildren="启用" unCheckedChildren="禁用" />
              </Form.Item>
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <Card title="定价设置" size="small">
              <Form.Item
                label="订阅时长（天）"
                name="duration_days"
                rules={[{ required: true, message: "请输入订阅时长" }]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={1}
                  max={3650}
                  placeholder="30"
                  addonAfter="天"
                />
              </Form.Item>

              <Form.Item
                label="价格（元）"
                name="price_cents"
                rules={[{ required: true, message: "请输入价格" }]}
                tooltip="请输入以元为单位的价格"
                getValueFromEvent={(value) => Math.round(value * 100)}
                getValueProps={(value) => ({ value: value / 100 })}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  max={99999}
                  precision={2}
                  placeholder="29.90"
                  addonBefore="¥"
                />
              </Form.Item>

              <Form.Item
                label="原价（元）"
                name="original_price_cents"
                tooltip="用于显示划线价，留空表示不显示原价"
                getValueFromEvent={(value) =>
                  value ? Math.round(value * 100) : null
                }
                getValueProps={(value) => ({
                  value: value ? value / 100 : undefined,
                })}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  max={99999}
                  precision={2}
                  placeholder="49.90"
                  addonBefore="¥"
                />
              </Form.Item>

              <Form.Item
                label="货币单位"
                name="currency"
                rules={[{ required: true }]}
              >
                <Select
                  options={[
                    { label: "人民币 (CNY)", value: "CNY" },
                    { label: "美元 (USD)", value: "USD" },
                  ]}
                />
              </Form.Item>
            </Card>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Card title="功能权限" size="small">
              <Space direction="vertical" style={{ width: "100%" }} size="large">
                <Text type="secondary">
                  选择该等级包含的功能权限（将存储为JSON格式）
                </Text>

                <Form.Item
                  name={["features", "hd_quality"]}
                  valuePropName="checked"
                  style={{ marginBottom: 8 }}
                >
                  <Checkbox>高清画质 (HD)</Checkbox>
                </Form.Item>

                <Form.Item
                  name={["features", "4k_quality"]}
                  valuePropName="checked"
                  style={{ marginBottom: 8 }}
                >
                  <Checkbox>超清画质 (4K)</Checkbox>
                </Form.Item>

                <Form.Item
                  name={["features", "ad_free"]}
                  valuePropName="checked"
                  style={{ marginBottom: 8 }}
                >
                  <Checkbox>无广告体验</Checkbox>
                </Form.Item>

                <Form.Item
                  name={["features", "offline_download"]}
                  valuePropName="checked"
                  style={{ marginBottom: 8 }}
                >
                  <Checkbox>离线下载</Checkbox>
                </Form.Item>

                <Form.Item
                  name={["features", "early_access"]}
                  valuePropName="checked"
                  style={{ marginBottom: 8 }}
                >
                  <Checkbox>抢先观看新片</Checkbox>
                </Form.Item>

                <Form.Item
                  label="每日下载次数"
                  name={["features", "max_downloads_per_day"]}
                  tooltip="-1表示无限制，0表示不允许下载"
                >
                  <InputNumber
                    style={{ width: 200 }}
                    min={-1}
                    max={1000}
                    placeholder="10"
                  />
                </Form.Item>

                <Form.Item
                  label="最大设备数"
                  name={["features", "max_devices"]}
                  tooltip="允许同时登录的设备数量"
                >
                  <InputNumber
                    style={{ width: 200 }}
                    min={1}
                    max={10}
                    placeholder="3"
                  />
                </Form.Item>
              </Space>
            </Card>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} lg={12}>
            <Card title="徽章设置（可选）" size="small">
              <Form.Item label="徽章文本" name="badge_text">
                <Input placeholder="例如: 推荐" maxLength={20} />
              </Form.Item>

              <Form.Item label="徽章颜色" name="badge_color">
                <Input
                  type="color"
                  placeholder="#FFD700"
                  style={{ width: 100 }}
                />
              </Form.Item>
            </Card>
          </Col>
        </Row>
      </Form>
    </Edit>
  );
};
